<template>
  <van-dialog
    v-model:show="showDialog"
    :title="isForce ? '需要更新' : '发现新版本'"
    :show-confirm-button="true"
    :show-cancel-button="!isForce"
    :close-on-click-overlay="!isForce"
    confirm-button-text="立即更新"
    cancel-button-text="稍后再说"
    @confirm="handleUpdate"
    @cancel="handleCancel"
    class="update-dialog"
  >
    <div class="update-content">
      <div class="version-info">
        <div class="current-version">当前版本：{{ currentVersion }}</div>
        <div class="latest-version">最新版本：{{ versionInfo.version }}</div>
      </div>

      <div class="update-notes" v-if="versionInfo.releaseNotes">
        <div class="notes-title">更新内容：</div>
        <div class="notes-content">{{ versionInfo.releaseNotes }}</div>
      </div>

      <div class="update-tip" v-if="isForce">
        <van-icon name="warning-o" color="#ff6034" />
        <span>此版本为重要更新，必须更新后才能继续使用</span>
      </div>
    </div>
  </van-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { showToast } from 'vant'
import { Browser } from '@capacitor/browser'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  versionInfo: {
    type: Object,
    default: () => ({})
  },
  currentVersion: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:show', 'update'])

const showDialog = computed({
  get: () => props.show,
  set: (val) => {
    // 强制更新不允许关闭弹窗
    if (isForce.value && val === false) {
      console.log('⚠️ 强制更新，不允许关闭弹窗')
      return
    }
    emit('update:show', val)
  }
})

const isForce = computed(() => props.versionInfo.isForce === 1)

// 立即更新
const handleUpdate = async () => {
  const updateUrl = props.versionInfo.updateUrl
  if (!updateUrl) {
    showToast('更新链接未配置')
    return
  }

  console.log('🔗 打开更新链接:', updateUrl)

  // iOS 平台使用 SystemPlugin 打开（避免 WebView 无法处理 itms-apps:// 协议）
  if (window.Capacitor && window.Capacitor.getPlatform() === 'ios') {
    console.log('📱 iOS 平台，使用 SystemPlugin.openSystemUrl 跳转')
    try {
      const { SystemPlugin } = await import('@/plugins/system')
      await SystemPlugin.openSystemUrl({ url: updateUrl })
      console.log('✅ 已打开 App Store')
      return
    } catch (error) {
      console.error('❌ SystemPlugin.openSystemUrl 失败：', error)
    }
  }

  // Android 或其他平台尝试用 Browser 插件打开
  if (window.Capacitor) {
    try {
      await Browser.open({ url: updateUrl })
      return
    } catch (e) {
      console.log('⚠️ Browser.open 失败:', e)
    }
  }

  // Web 环境用 window.open 打开
  window.open(updateUrl, '_blank')
}

// 稍后再说（非强制更新）
const handleCancel = () => {
  // 强制更新不允许取消
  if (isForce.value) {
    showToast('此版本必须更新后才能继续使用')
    return
  }
  showDialog.value = false
}
</script>

<style scoped>
.update-dialog {
  --van-dialog-border-radius: 16px;
}

.update-content {
  padding: 20px;
}

.version-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: #f7f8fa;
  border-radius: 8px;
}

.current-version,
.latest-version {
  font-size: 14px;
  color: #333;
}

.latest-version {
  color: #1989fa;
  font-weight: 500;
}

.update-notes {
  margin-bottom: 16px;
}

.notes-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.notes-content {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  white-space: pre-line;
  padding: 12px;
  background-color: #f7f8fa;
  border-radius: 8px;
}

.update-tip {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #fff8f8;
  border-radius: 8px;
  font-size: 13px;
  color: #ff6034;
}

.update-tip .van-icon {
  margin-right: 8px;
  font-size: 18px;
}
</style>
